// Product and set identity comes from exact SKUs and quantities, not decor labels.
function componentKey(parts) {
  const quantities = new Map();
  for (const part of parts) quantities.set(String(part.sku), (quantities.get(String(part.sku)) || 0) + Number(part.quantity));
  return JSON.stringify([...quantities].sort(([a], [b]) => a.localeCompare(b)));
}
function selectedComponents(catalog, state) {
  const components = state.mode === 'set' ? catalog.components : catalog.components.filter(component => component.key === state.mode);
  return components.map(component => ({ ...component, item: component.products.find(item => item.sku === state.choices[component.key]) }));
}
function selectionItems(catalog, state) {
  const components = selectedComponents(catalog, state);
  if (state.mode !== 'set') return components.map(component => component.item);
  if (!components.length || components.some(component => !component.item)) return [undefined];
  const key = componentKey(components.map(component => ({ sku: component.item.sku, quantity: component.quantity })));
  return [catalog.sets.find(item => componentKey(item.parts) === key)];
}
function quantityAvailable(item, quantity) {
  return !!item?.available && Number.isInteger(quantity) && quantity > 0 &&
    (item.stockLimit == null || quantity <= item.stockLimit);
}
function selectionAvailable(catalog, state) {
  const components = selectedComponents(catalog, state);
  const items = selectionItems(catalog, state);
  if (!components.length || items.length !== 1 || !items[0]?.id ||
      !quantityAvailable(items[0], state.mode === 'set' ? 1 : components[0].quantity)) return false;
  const required = new Map();
  for (const component of components) {
    if (!quantityAvailable(component.item, component.quantity)) return false;
    const key = component.item.sku;
    const quantity = (required.get(key) || 0) + component.quantity;
    if (!quantityAvailable(component.item, quantity)) return false;
    required.set(key, quantity);
  }
  return true;
}
function shippingIsFree(price, currency, country, settings) {
  return currency === settings.currency && country === settings.country && price >= settings.threshold && settings.threshold > 0;
}
function upsellSelection(catalog, original, choices = {}) {
  if (!original || original.mode === 'set' || catalog.components.length < 2) return null;
  const selected = selectedComponents(catalog, original)[0];
  if (!selected?.item) return null;
  const next = { mode: 'set', choices: { ...original.choices, ...choices, [selected.key]: selected.item.sku } };
  const extras = catalog.components.filter(component => component.key !== selected.key);
  for (const component of extras) {
    if (!choices[component.key]) next.choices[component.key] = (
      component.products.find(item => item.code === selected.item.code && quantityAvailable(item, component.quantity)) ||
      component.products.find(item => item.sku === original.choices[component.key] && quantityAvailable(item, component.quantity)) ||
      component.products.find(item => quantityAvailable(item, component.quantity))
    )?.sku;
  }
  const item = selectionItems(catalog, next)[0];
  const components = selectedComponents(catalog, next);
  const reference = components.reduce((sum, component) => sum + (component.item?.price || 0) * component.quantity, 0);
  return { state: next, item, components, extras, available: selectionAvailable(catalog, next),
    extra: item ? item.price - selected.item.price * selected.quantity : null,
    savings: item ? reference - item.price : 0 };
}
function additionLine(cart, item) {
  const exact = cart.items.find(line => line.key === item.key);
  if (exact) return exact;
  const matches = cart.items.filter(line => String(line.variant_id || line.id) === String(item.variant_id || item.id) &&
    JSON.stringify(line.properties || {}) === JSON.stringify(item.properties || {}));
  return matches.length === 1 ? matches[0] : null;
}
class LandingSelection {
  constructor(root) {
    this.root = root;
    this.catalog = JSON.parse(root.querySelector('[data-products]').textContent);
    this.labels = JSON.parse(root.querySelector('[data-labels]').textContent);
    this.events = new AbortController();
    this.money = new Intl.NumberFormat(root.dataset.locale, { style: 'currency', currency: root.dataset.currency });
    this.catalog.sets ??= [];
    this.state = { mode: this.catalog.components[0]?.key, choices: {} };
    for (const component of this.catalog.components) {
      component.products = [...new Map(component.products.map(item => [item.sku, item])).values()].sort((a, b) => a.code.localeCompare(b.code));
      this.state.choices[component.key] = (component.products.find(item => item.code === component.defaultDecor) || component.products.find(item => item.available) || component.products[0])?.sku;
    }
    const options = { signal: this.events.signal };
    root.addEventListener('change', event => {
      if (event.target.matches('[data-upsell-decor]')) {
        this.upsellChoices[event.target.dataset.upsellDecor] = event.target.value;
        this.renderUpsell();
        return;
      }
      if (event.target.matches('[data-component-decor]')) this.state.choices[event.target.dataset.componentDecor] = event.target.value;
      if (event.target.matches('[data-selection-mode]')) this.state.mode = event.target.value;
      this.error = '';
      this.render();
    }, options);
    root.querySelector('[data-purchase-form]').addEventListener('submit', event => { event.preventDefault(); this.add(); }, options);
    const dialog = root.querySelector('[data-cart-dialog]');
    root.querySelector('[data-upgrade-set]').addEventListener('click', () => this.upgrade());
    root.querySelectorAll('[data-close-dialog]').forEach(button => button.addEventListener('click', () => dialog.close(), options));
    dialog.addEventListener('click', event => { if (event.target === dialog && !this.upgrading) dialog.close(); }, options);
    dialog.addEventListener('cancel', event => { if (this.upgrading) event.preventDefault(); }, options);
    dialog.querySelector('a').addEventListener('click', event => { if (this.upgrading) event.preventDefault(); }, options);
    const sticky = root.nextElementSibling;
    const hero = document.querySelector('.hero');
    if (sticky?.matches('.sticky') && hero) {
      this.visibility = new IntersectionObserver(() => {
        const bounds = root.getBoundingClientRect();
        sticky.classList.toggle('show', hero.getBoundingClientRect().bottom < 0 && (bounds.bottom < 0 || bounds.top > innerHeight));
      });
      this.visibility.observe(hero); this.visibility.observe(root);
    }
    this.render();
    if (root.dataset.setsUrl) this.loadSets();
  }
  async loadSets() {
    this.lookup?.abort();
    const lookup = new AbortController();
    this.lookup = lookup;
    this.loadingSets = true;
    this.catalogError = '';
    this.render();
    try {
      let url = new URL(this.root.dataset.setsUrl, location.origin);
      const origin = url.origin;
      const visited = new Set();
      const sets = new Map(this.catalog.sets.map(item => [item.id, item]));
      while (url) {
        if (url.origin !== origin || visited.has(url.href)) throw new Error('Invalid collection pagination');
        visited.add(url.href);
        const response = await fetch(url.href, { signal: lookup.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Collection request failed');
        const page = await response.json();
        if (!Array.isArray(page.products)) throw new Error('Invalid collection products');
        for (const product of page.products) {
          if (product.id && product.parts?.length) sets.set(product.id, product);
        }
        if (!page.nextPage) url = null;
        else {
          const next = new URL(page.nextPage, url);
          next.searchParams.set('view', 'hb-landing-products');
          url = next;
        }
      }
      if (!lookup.signal.aborted) this.catalog.sets = [...sets.values()];
    } catch (error) {
      if (!lookup.signal.aborted) this.catalogError = this.labels.error;
    } finally {
      if (this.lookup === lookup && !lookup.signal.aborted) { this.loadingSets = false; this.render(); if (this.lastAddition) this.renderUpsell(); }
    }
  }
  render() {
    const components = selectedComponents(this.catalog, this.state);
    const pair = this.state.mode === 'set';
    const item = selectionItems(this.catalog, this.state)[0];
    const available = selectionAvailable(this.catalog, this.state);
    const total = (item?.price || 0) * (pair ? 1 : components[0]?.quantity || 1);
    for (const component of this.catalog.components) {
      const picker = this.root.querySelector(`[data-component-picker="${component.key}"]`);
      picker.hidden = !pair && this.state.mode !== component.key;
      const select = picker.querySelector('select');
      const chosen = component.products.find(product => product.sku === this.state.choices[component.key]);
      select.replaceChildren(...component.products.map(product => {
        const option = document.createElement('option');
        option.value = product.sku; option.textContent = `${this.labels.decor} ${product.label}${!quantityAvailable(product, component.quantity) ? ` · ${this.labels.unavailable}` : ''}`;
        option.selected = product.sku === chosen?.sku;
        return option;
      }));
      const thumb = picker.querySelector('img');
      thumb.hidden = !chosen?.image;
      if (chosen?.image) { thumb.src = chosen.image; thumb.alt = chosen.title; }
      const price = this.root.querySelector(`[data-component-price="${component.key}"]`);
      price.textContent = chosen ? this.money.format(chosen.price * component.quantity / 100) : '—';
    }
    const photos = this.root.querySelector('[data-selected-photos]');
    photos.classList.toggle('show-pair', pair);
    photos.replaceChildren(...components.filter(component => component.item?.image).map(component => {
      const image = document.createElement('img'); image.src = component.item.image; image.alt = `${component.label} · ${component.item.title}`; image.width = 800; image.height = 800; return image;
    }));
    this.root.querySelector('[data-image-label]').textContent = components.map(component => `${component.label} · ${component.item?.label || this.labels.missing}`).join(' / ');
    for (const element of this.root.querySelectorAll('[data-price], [data-total]')) element.textContent = item ? this.money.format(total / 100) : '—';
    for (const element of this.root.nextElementSibling?.querySelectorAll('[data-sticky-price]') || []) element.textContent = item ? this.money.format(total / 100) : '—';
    const reference = pair ? components.reduce((sum, component) => sum + (component.item?.price || 0) * component.quantity, 0) : total;
    const savings = this.root.querySelector('[data-savings]');
    savings.hidden = !item || !pair || reference <= total;
    savings.querySelector('del').textContent = this.money.format(reference / 100);
    savings.querySelector('span').textContent = this.labels.savings.replace('__amount__', this.money.format((reference - total) / 100));
    const shipping = this.root.querySelector('[data-shipping]');
    shipping.textContent = item && this.hasFreeShipping(total) ? this.labels.freeShipping : this.labels.shipping;
    this.root.querySelector('[data-variant-id]').value = item?.id || '';
    this.root.querySelector('[data-quantity]').value = pair ? 1 : components[0]?.quantity || 1;
    this.root.querySelector('[data-add]').disabled = !available || this.pending;
    this.root.querySelector('[data-add-label]').textContent = this.pending ? this.labels.adding : available ? this.labels.add : pair && this.loadingSets ? this.labels.loading : this.labels.unavailable;
    this.root.querySelector('[data-availability]').textContent = this.error || (pair && this.loadingSets ? this.labels.loading : pair && this.catalogError ? this.catalogError : available ? components.map(component => component.item.delivery).filter(Boolean).join(' / ') : this.labels.unavailable);
    this.root.querySelector('[data-pair-price]').textContent = pair && item ? this.money.format(total / 100) : this.labels.selectSet;
    for (const radio of this.root.querySelectorAll('[data-selection-mode]')) radio.checked = radio.value === this.state.mode;
  }
  async add() {
    if (this.pending) return;
    const items = selectionItems(this.catalog, this.state);
    const components = selectedComponents(this.catalog, this.state);
    if (!selectionAvailable(this.catalog, this.state)) return;
    this.pending = true;
    const state = { mode: this.state.mode, choices: { ...this.state.choices } };
    const status = this.root.querySelector('[data-purchase-status]');
    status.textContent = '';
    this.render();
    try {
      const quantity = state.mode === 'set' ? 1 : components[0].quantity;
      const response = await fetch(this.root.dataset.addUrl, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items.map(item => ({ id: item.id, quantity })) })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : this.labels.error);
      status.textContent = this.labels.added;
      this.lastAddition = { state, items: result.items.map(item => ({ ...item, quantity, final_line_price: item.final_price * quantity })) };
      this.upsellChoices = {};
      this.upgradeBlocked = false;
      this.cart = null;
      try { this.cart = await this.cartRequest(); this.updateCartCount(this.cart); } catch { /* The addition still succeeded. */ }
      // Shopify returns cumulative cart-line quantities; confirm only this addition.
      try { this.showAdded(this.lastAddition.items); } catch {
        const link = document.createElement('a'); link.href = this.root.dataset.cartUrl; link.textContent = this.labels.added; status.replaceChildren(link);
      }
    } catch (error) { status.textContent = error.message || this.labels.error; status.focus(); }
    finally { this.pending = false; this.render(); }
  }
  showAdded(items) {
    const container = this.root.querySelector('[data-added-items]');
    container.replaceChildren(...items.map(item => {
      const row = document.createElement('div'); row.className = 'cart-item';
      const image = document.createElement('img'); image.src = item.featured_image?.url || item.image; image.alt = item.product_title; image.width = 84; image.height = 84;
      const copy = document.createElement('div'); const title = document.createElement('strong'); title.textContent = item.product_title;
      const variant = document.createElement('p'); variant.textContent = `${item.quantity} × ${item.variant_title}`; copy.append(title, variant);
      const price = document.createElement('strong'); price.textContent = this.money.format(item.final_line_price / 100);
      row.append(image, copy, price); return row;
    }));
    this.root.querySelector('[data-added-total]').textContent = this.money.format(items.reduce((sum, item) => sum + item.final_line_price, 0) / 100);
    const shipping = this.root.querySelector('[data-cart-shipping]');
    const free = this.cart && this.hasFreeShipping(this.cart.total_price, this.cart.currency);
    shipping.textContent = free ? this.labels.freeShipping : this.labels.shipping;
    shipping.classList.toggle('is-free', !!free);
    this.root.querySelector('[data-upsell-status]').textContent = '';
    this.renderUpsell();
    const dialog = this.root.querySelector('[data-cart-dialog]');
    if (!dialog.open) dialog.showModal();
  }
  hasFreeShipping(price, currency = this.root.dataset.currency) {
    return shippingIsFree(price, currency, this.root.dataset.country, {
      currency: 'EUR', country: this.root.dataset.freeShippingCountry, threshold: Number(this.root.dataset.freeShippingThreshold)
    });
  }
  async cartRequest(path = '', body) {
    const response = await fetch(`${this.root.dataset.cartUrl}${path}.js`, body === undefined ? undefined : {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body)
    });
    const result = await response.json();
    if (!response.ok) {
      const error = new Error(typeof result.description === 'string' ? result.description : this.labels.error);
      error.cartRejected = true;
      throw error;
    }
    return result;
  }
  updateCartCount(cart) {
    document.querySelectorAll('[data-hb-cart-count]').forEach(badge => { badge.textContent = cart.item_count; badge.hidden = cart.item_count === 0; });
  }
  renderUpsell() {
    const area = this.root.querySelector('[data-upsell]');
    this.root.querySelectorAll('[data-close-dialog]').forEach(button => { button.disabled = !!this.upgrading; });
    const offer = this.root.dataset.enableUpsell === 'true' && !this.upgradeBlocked ? upsellSelection(this.catalog, this.lastAddition?.state, this.upsellChoices) : null;
    area.hidden = !offer;
    if (!offer) return;
    this.offer = offer;
    const pickers = this.root.querySelector('[data-upsell-pickers]');
    pickers.replaceChildren(...offer.extras.map(component => {
      const label = document.createElement('label'); label.className = 'decor-picker';
      const heading = document.createElement('strong'); heading.textContent = component.upsellHeading || component.label;
      const select = document.createElement('select'); select.dataset.upsellDecor = component.key;
      select.setAttribute('aria-label', `${component.label} · ${this.labels.decor}`); select.disabled = !!this.upgrading;
      select.replaceChildren(...component.products.map(item => {
        const option = document.createElement('option'); option.value = item.sku;
        option.textContent = `${this.labels.decor} ${item.label}${!quantityAvailable(item, component.quantity) ? ` · ${this.labels.unavailable}` : ''}`;
        option.selected = offer.state.choices[component.key] === item.sku;
        return option;
      }));
      label.append(heading, select); return label;
    }));
    this.root.querySelector('[data-upsell-photos]').replaceChildren(...offer.components.filter(component => component.item?.image).map(component => {
      const image = document.createElement('img'); image.src = component.item.image; image.alt = component.item.title; image.width = 600; image.height = 600; return image;
    }));
    const price = this.root.querySelector('[data-upsell-price]');
    price.textContent = offer.item ? this.labels.setPrice.replace('__amount__', this.money.format(offer.item.price / 100)) : this.loadingSets ? this.labels.loading : this.labels.unavailable;
    const savings = this.root.querySelector('[data-upsell-savings]');
    savings.textContent = offer.savings > 0 ? this.labels.savings.replace('__amount__', this.money.format(offer.savings / 100)) : '';
    const button = this.root.querySelector('[data-upgrade-set]');
    button.disabled = this.upgrading || !offer.available;
    button.textContent = this.upgrading ? this.labels.adding : !offer.available ? this.labels.unavailable :
      `${this.labels.completeSet} · ${offer.extra >= 0 ? '+ ' : '− '}${this.money.format(Math.abs(offer.extra) / 100)}`;
  }
  async upgrade() {
    if (this.upgrading || this.upgradeBlocked || !this.lastAddition) return;
    const offer = upsellSelection(this.catalog, this.lastAddition.state, this.upsellChoices);
    if (!offer?.available) return;
    this.upgrading = true;
    this.renderUpsell();
    const status = this.root.querySelector('[data-upsell-status]'); status.textContent = '';
    const original = this.lastAddition.items[0];
    let addedSet, addingSet = false, replaced = false;
    try {
      const before = await this.cartRequest();
      const originalLine = additionLine(before, original);
      if (!originalLine || originalLine.quantity < original.quantity) throw new Error(this.labels.cartChanged);
      const existing = before.items.filter(item => String(item.variant_id) === String(offer.item.id)).reduce((sum, item) => sum + item.quantity, 0);
      if (!quantityAvailable(offer.item, existing + 1)) throw new Error(this.labels.unavailable);
      // Add first: a failed set addition must leave the purchased single product intact.
      addingSet = true;
      const result = await this.cartRequest('/add', { items: [{ id: offer.item.id, quantity: 1 }] });
      addingSet = false;
      addedSet = result.items[0];
      const current = await this.cartRequest();
      const line = additionLine(current, original);
      if (!line || line.quantity < original.quantity) throw new Error(this.labels.cartChanged);
      this.cart = await this.cartRequest('/change', { id: line.key, quantity: line.quantity - original.quantity });
      replaced = true;
      const confirmed = additionLine(this.cart, addedSet) || addedSet;
      this.lastAddition = { state: offer.state, items: [{ ...confirmed, quantity: 1, final_line_price: confirmed.final_price }] };
      this.updateCartCount(this.cart);
      this.showAdded(this.lastAddition.items);
    } catch (error) {
      if (addingSet && !error.cartRejected) this.upgradeBlocked = true;
      if (addedSet && !replaced) {
        try {
          const current = await this.cartRequest();
          const line = additionLine(current, addedSet);
          if (!line) throw new Error(this.labels.cartChanged);
          this.cart = await this.cartRequest('/change', { id: line.key, quantity: line.quantity - 1 });
          this.updateCartCount(this.cart);
        } catch { this.upgradeBlocked = true; }
      }
      status.textContent = this.upgradeBlocked ? this.labels.cartChanged : error.message || this.labels.error;
      status.focus();
    } finally { this.upgrading = false; this.renderUpsell(); }
  }
  destroy() { this.events.abort(); this.lookup?.abort(); this.visibility?.disconnect(); }
}

class LandingGallery {
  constructor(root) {
    this.events = new AbortController();
    const options = { signal: this.events.signal };
    const track = root.querySelector('[data-gallery-track]');
    const previous = root.querySelector('[data-gallery-prev]');
    const next = root.querySelector('[data-gallery-next]');
    const count = root.querySelector('[data-gallery-count]');
    const move = direction => track.scrollBy({ left: direction * (track.firstElementChild?.getBoundingClientRect().width || track.clientWidth), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    const update = () => {
      const slides = [...track.children];
      const left = track.getBoundingClientRect().left;
      const index = slides.reduce((best, slide, i) => Math.abs(slide.getBoundingClientRect().left - left) < Math.abs(slides[best].getBoundingClientRect().left - left) ? i : best, 0);
      count.textContent = `${slides.length ? index + 1 : 0} / ${slides.length}`;
      previous.disabled = track.scrollLeft <= 1;
      next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
    };
    previous.addEventListener('click', () => move(-1), options);
    next.addEventListener('click', () => move(1), options);
    track.addEventListener('scroll', update, options);
    track.addEventListener('keydown', event => {
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') { event.preventDefault(); move(event.key === 'ArrowRight' ? 1 : -1); }
    }, options);
    window.addEventListener('resize', update, options);
    update();
  }
  destroy() { this.events.abort(); }
}

const landingInstances = new WeakMap();
function initializeLanding(container) {
  const roots = container.matches?.('[data-hb-selection], [data-hb-gallery]') ? [container] : container.querySelectorAll('[data-hb-selection], [data-hb-gallery]');
  for (const root of roots) if (!landingInstances.has(root)) landingInstances.set(root, root.matches('[data-hb-selection]') ? new LandingSelection(root) : new LandingGallery(root));
}
initializeLanding(document);
document.addEventListener('shopify:section:load', event => initializeLanding(event.target));
document.addEventListener('shopify:section:unload', event => {
  for (const root of event.target.querySelectorAll('[data-hb-selection], [data-hb-gallery]')) { landingInstances.get(root)?.destroy(); landingInstances.delete(root); }
});
